// Microbe RTS — base defense: every owned node defends itself.
//
// TIER 1 (always on): a biological "ooze" — a contact poison aura filling the node's
// interaction radius. An enemy that touches it is poisoned: a fixed dps for a fixed
// duration (config.BASE_DEFENSE). While the enemy stays in contact the poison is RE-applied
// on a fixed cadence; because the re-dose cadence is shorter than the duration, the debuff
// never lapses while touching, so consecutive doses accumulate. Doses 1..(envenom-1) just
// refresh (non-stacking, tankable). The envenom-th consecutive dose flips it into a STACKING
// "envenomed" state that ramps each further dose. The counter resets ONLY when the poison
// fully lapses (enemy leaves and the duration expires) — so "pull out and let it wear off"
// is the intended counterplay, and it needs no extra bookkeeping.
//
// TIER 2 (owner colony.tier >= rangedTier): the node additionally fires a ranged
// single-target PHYSICAL shot at the nearest enemy, on a cooldown.
//
// Home is the strongest defender; captured colony nodes are noticeably weaker (per config).
// All numbers live in config.BASE_DEFENSE; nothing here hardcodes a tunable.

import type { GameState, Unit, WorldNode, Side } from '../types.js';
import { BASE_DEFENSE, QUEEN, TURRET } from '../config.js';
import { dist, unitSpec } from './world.js';
import { cleanupDead } from './combat.js';

/** A node only defends if a colony owns it. */
function defenderSide(n: WorldNode): Side | null {
  return n.owner === 'you' || n.owner === 'bot' ? n.owner : null;
}

/** Per-stack poison dps for a node (Home strongest, colony weaker). */
function nodePoisonDps(n: WorldNode): number {
  return n.type === 'home' ? BASE_DEFENSE.homePoisonDps : BASE_DEFENSE.colonyPoisonDps;
}

/** Apply one poison dose to a unit, escalating past the envenom threshold. */
function applyDose(u: Unit, dps: number): void {
  const BD = BASE_DEFENSE;
  u.poisonDose += 1;
  u.poisonTimer = BD.poisonDuration; // refresh to full
  u.poisonDps = dps;
  if (u.poisonDose < BD.envenomAtDose) {
    u.poisonStacks = 1; // normal poison — fixed dps, non-stacking
  } else if (u.poisonDose === BD.envenomAtDose) {
    u.poisonStacks = 2; // the escalating dose: begin stacking
  } else {
    u.poisonStacks = Math.min(BD.poisonMaxStacks, u.poisonStacks + 1);
  }
}

export function resolveBaseDefense(state: GameState, dt: number): void {
  const BD = BASE_DEFENSE;

  // Fade + drain last frame's tracers.
  if (state.shots.length) {
    for (const s of state.shots) s.ttl -= dt;
    state.shots = state.shots.filter((s) => s.ttl > 0);
  }

  // ---- Tier 1: contact poison ooze --------------------------------------
  // For each enemy unit, find the STRONGEST hostile aura it is touching this tick
  // (a unit inside two enemy nodes takes the harsher one).
  const contactDps = new Map<string, number>();
  for (const n of state.nodes) {
    const side = defenderSide(n);
    if (!side) continue;
    const reach = n.radius + BD.auraPadding;
    const dps = nodePoisonDps(n);
    for (const u of state.units) {
      // Skip the owner's own units AND the neutral wild fauna (grazers are toxin-adapted and
      // farmable — a base must not silently grind one to death for zero amino/salvage).
      if (u.hp <= 0 || u.side === side || u.side === 'wild') continue;
      if (dist(u.pos, n.pos) <= reach) {
        if (dps > (contactDps.get(u.id) ?? 0)) contactDps.set(u.id, dps);
      }
    }
  }

  // Home turrets emit their own poison cloud (same envenom system). Only ACTIVE turrets (alive + not
  // mid-build/repair) exude; a unit in overlapping auras takes the strongest.
  for (const t of state.turrets) {
    if (t.hp <= 0 || t.timer > 0) continue;
    const reach = TURRET.radius + TURRET.auraRadius;
    for (const u of state.units) {
      if (u.hp <= 0 || u.side === t.side || u.side === 'wild') continue;
      if (dist(u.pos, t.pos) <= reach) {
        if (TURRET.poisonDps > (contactDps.get(u.id) ?? 0)) contactDps.set(u.id, TURRET.poisonDps);
      }
    }
  }

  const doseThreshold = BD.poisonDuration - BD.reapplySeconds; // re-dose once timer decays past this
  for (const u of state.units) {
    if (u.hp <= 0) continue;

    // Tick existing poison damage + decay.
    if (u.poisonTimer > 0) {
      u.hp -= u.poisonDps * u.poisonStacks * dt;
      u.poisonTimer -= dt;
    }

    // Re-dose on cadence while in contact (also the first dose on a clean unit, timer<=0).
    const contact = contactDps.get(u.id);
    if (contact !== undefined && u.poisonTimer <= doseThreshold) {
      applyDose(u, contact);
    }

    // Full lapse -> reset the escalation counter.
    if (u.poisonTimer <= 0) {
      u.poisonTimer = 0;
      u.poisonDose = 0;
      u.poisonStacks = 0;
      u.poisonDps = 0;
    }
  }

  // ---- Tier 2: ranged single-target physical ----------------------------
  for (const n of state.nodes) {
    const side = defenderSide(n);
    if (!side) continue;
    if (state.colonies[side].tier < BD.rangedTier) {
      n.fireCooldown = 0;
      continue;
    }
    n.fireCooldown = Math.max(0, n.fireCooldown - dt);
    if (n.fireCooldown > 0) continue;

    const range = n.type === 'home' ? BD.homeRange : BD.colonyRange;
    let best: Unit | null = null;
    let bestD = Infinity;
    for (const u of state.units) {
      if (u.hp <= 0 || u.side === side || u.side === 'wild') continue; // never fire on neutral fauna
      const d = dist(u.pos, n.pos);
      if (d <= range && d < bestD) {
        bestD = d;
        best = u;
      }
    }
    if (!best) continue;

    const dmg = n.type === 'home' ? BD.homeShotDmg : BD.colonyShotDmg;
    best.hp -= Math.max(1, dmg - unitSpec(best.kind).armor - (best.queen ? QUEEN.armor : 0));
    n.fireCooldown = n.type === 'home' ? BD.homeShotInterval : BD.colonyShotInterval;
    state.shots.push({ from: { ...n.pos }, to: { ...best.pos }, ttl: BD.shotTtl, side });
  }

  // Reap anything the ooze or the shots just killed.
  cleanupDead(state);
}
